import { useMemo } from "react";
import { useLocSettings } from "../../hooks/SettingsHooks";
import ProductPreview, {
  ProductPreviewLoadingPlaceholder,
  ProductPreviewPlaceholder
} from "../ProductPreview";
import ProductListItem, {
  ProductListItemPlaceholder
} from "../ProductListItem";
import classNames from "classnames";
import propTypes from "prop-types";
import { AnimatePresence, motion } from "motion/react";
import { SORT_BY } from "../../utils/ProductsUtils";
import { getTranslationFunction } from "../../utils/i18nUtils";
import { useUser } from "../../functional-components/UserContext";
import { useSpecsTranslation } from "../../hooks/TranslationHooks";
import COLORS from "../../config/colors";
import RenderWhenInView from "../../functional-components/RenderWhenInView";
import "./style.scss";

function groupProducts(products, field) {
  const t = getTranslationFunction();
  return products.reduce((res, product) => {
    const value =
      product.specsIndexed?.[field]?.value || t("product-group.no-value");
    if (!(value in res)) res[value] = [];
    res[value].push(product);
    return res;
  }, {});
}

const ProductGrid = ({
  id,
  style,
  products,
  preferredCategory,
  groupBy = 0,
  layout = "card",
  showCartButtons = false,
  clickToFilter = false,
  b2b: b2bProp = false,
  noSlideShow = false,
  noOverlay = false,
  showPricesWithVat = undefined
}) => {
  const settings = useLocSettings();
  const { b2b: userB2b } = useUser();
  const b2b = useMemo(() => b2bProp || userB2b, [b2bProp, userB2b]);
  const ts = useSpecsTranslation();

  let groupField;
  switch (groupBy) {
    case SORT_BY.grade:
      groupField = "Grading";
      break;
    case SORT_BY.color:
      groupField = "Staining";
      break;
    default:
  }

  const groupedProducts = useMemo(
    () =>
      products
        ? groupField
          ? groupProducts(products, groupField)
          : { "(all)": products }
        : { "(all)": [] },
    [groupField, products]
  );

  const icons = groupBy ? settings.specifications[groupField] : false;

  //console.log("groupedProducts", groupedProducts);

  return (
    <motion.div
      id={id}
      className="product-grid-wrapper"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1
        }
      }}
      initial="hidden"
      animate="visible"
      exit="hidden"
      style={style}
      layout={false}
    >
      <AnimatePresence initial={false}>
        {Object.entries(groupedProducts).map(([value, products]) => (
          <motion.div
            className="product-group"
            key={value}
            layout={false}
            initial={{
              opacity: 0
            }}
            animate={{
              opacity: 1
            }}
            exit={{
              opacity: 0,
              transitionEnd: { display: "none" }
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30
            }}
          >
            {value !== "(all)" && (
              <h3 className="product-group-header">
                {icons?.[value]?.icon_svg && (
                  <>
                    <span
                      className={"spec-icon"}
                      style={{
                        color: icons[value].color || COLORS.bjelin_grey
                      }}
                      dangerouslySetInnerHTML={{
                        __html: icons[value].icon_svg
                      }}
                    />{" "}
                  </>
                )}
                <span>{ts(groupField, value)}</span>
              </h3>
            )}
            <div
              role="list"
              className={classNames(
                { "product-grid": layout === "card" },
                { "product-list": layout === "list" }
              )}
            >
              {layout === "list"
                ? products.map(product => (
                    <RenderWhenInView
                      key={product.sku}
                      placeholderProps={{ product }}
                      Placeholder={ProductListItemPlaceholder}
                    >
                      <ProductListItem
                        product={product}
                        preferredCategory={preferredCategory}
                        b2b={b2b}
                        showCartButton={showCartButtons}
                        compact={showCartButtons}
                        showPricesWithVat={showPricesWithVat}
                        noOverlay={noOverlay}
                      />
                    </RenderWhenInView>
                  ))
                : products.map(product => (
                    <RenderWhenInView
                      key={product.sku}
                      placeholderProps={{ product }}
                      Placeholder={ProductPreviewPlaceholder}
                    >
                      <ProductPreview
                        product={product}
                        preferredCategory={preferredCategory}
                        layout={layout}
                        noSlideShow={noSlideShow}
                        noOverlay={noOverlay}
                        showCartButton={showCartButtons}
                        clickToFilter={clickToFilter}
                        b2b={b2b}
                      />
                    </RenderWhenInView>
                  ))}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );
};

ProductGrid.propTypes = {
  id: propTypes.string,
  className: propTypes.string,
  style: propTypes.object,
  products: propTypes.array.isRequired,
  preferredCategory: propTypes.object,
  cols: propTypes.oneOf([2, 3, 4]),
  layout: propTypes.oneOf(["card", "list"]),
  groupBy: propTypes.number,
  showCartButtons: propTypes.bool,
  clickToFilter: propTypes.bool,
  noSlideShow: propTypes.bool
};

export default ProductGrid;

export const ProductGridLoadingPlaceholder = ({ layout = "card" }) => {
  return (
    <div className="product-grid-wrapper">
      <motion.div
        className={classNames(
          "products-grid-loading-group",
          "layout-" + layout
        )}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              when: "beforeChildren",
              staggerChildren: 0.2
            }
          }
        }}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <ProductPreviewLoadingPlaceholder layout={layout} />
        <ProductPreviewLoadingPlaceholder layout={layout} />
        <ProductPreviewLoadingPlaceholder layout={layout} />
      </motion.div>
    </div>
  );
};

ProductGridLoadingPlaceholder.propTypes = {
  layout: propTypes.oneOf(["card", "list"])
};
